import React from 'react';
import { Star } from 'lucide-react';
import { format } from 'date-fns';

interface Review {
  id: string;
  reviewer_id: string;
  reviewer: {
    display_name?: string;
    full_name?: string;
    avatar_url?: string;
  };
  rating: number;
  comment: string;
  created_at: string;
  helpful_count: number;
  response?: {
    response: string;
    created_at: string;
  };
}

interface ReviewsListProps {
  reviews: Review[];
  loading: boolean;
}

function ReviewsList({ reviews, loading }: ReviewsListProps) {
  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-white rounded-lg p-6 animate-pulse">
            <div className="flex items-start space-x-4">
              <div className="h-10 w-10 bg-gray-200 rounded-full" />
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded w-1/4 mb-2" />
                <div className="h-4 bg-gray-200 rounded w-1/3 mb-4" />
                <div className="h-4 bg-gray-200 rounded w-full mb-2" />
                <div className="h-4 bg-gray-200 rounded w-2/3" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (reviews.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No reviews yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {reviews.map((review) => (
        <div key={review.id} className="bg-white rounded-lg p-6">
          <div className="flex items-start space-x-4">
            {review.reviewer.avatar_url ? (
              <img
                src={review.reviewer.avatar_url}
                alt={review.reviewer.full_name || 'Reviewer'}
                className="h-10 w-10 rounded-full object-cover"
              />
            ) : (
              <div className="h-10 w-10 bg-gray-100 rounded-full flex items-center justify-center">
                <span className="text-gray-500 text-lg">
                  {(review.reviewer.display_name || review.reviewer.full_name || 'M')[0].toUpperCase()}
                </span>
              </div>
            )}
            
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">
                    {review.reviewer.display_name || review.reviewer.full_name || 'Member'}
                  </p>
                  <div className="flex items-center mt-1">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`h-4 w-4 ${
                          i < review.rating
                            ? 'text-yellow-400 fill-current'
                            : 'text-gray-300'
                        }`}
                      />
                    ))}
                    <span className="ml-2 text-sm text-gray-500">
                      {format(new Date(review.created_at), 'MMM d, yyyy')}
                    </span>
                  </div>
                </div>
                {review.helpful_count > 0 && (
                  <div className="text-sm text-gray-500">
                    {review.helpful_count} found helpful
                  </div>
                )}
              </div>
              
              <p className="mt-4 text-gray-600">{review.comment}</p>

              {review.response && (
                <div className="mt-4 pl-4 border-l-2 border-gray-200">
                  <p className="font-medium text-gray-900">Seller's response:</p>
                  <p className="mt-1 text-gray-600">{review.response.response}</p>
                  <p className="mt-1 text-sm text-gray-500">
                    {format(new Date(review.response.created_at), 'MMM d, yyyy')}
                  </p>
                </div>
              )}

              <div className="mt-4 flex items-center space-x-4">
                <button className="text-sm text-gray-500 hover:text-gray-700">
                  Helpful
                </button>
                <button className="text-sm text-gray-500 hover:text-gray-700">
                  Report
                </button>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default ReviewsList;